import React from "react";
import { ComponentConfig } from "@measured/puck";
import { IOrganizationProps } from "../../../types/Organization";
import { ISmallEventProps } from "../../../types/Event";
import { booleanField } from "../helpers";
import { useRootProps } from "../context";

// Organization Hero Component
export const OrganizationHero: ComponentConfig<{
  showLogo: boolean;
  showName: boolean;
  showSubtitle: boolean;
  showDescription: boolean;
  showCTA: boolean;
  ctaText: string;
  ctaLink: string;
  backgroundImage?: string;
  overlay: boolean;
  overlayOpacity: number;
  backgroundColor: string;
  textColor: string;
  alignX: "left" | "center" | "right";
  alignY: "top" | "center" | "bottom";
  contentWidth: number;
  gap: number;
  items: { title: string; body: string; icon: string }[];
}> = {
  fields: {
    showLogo: { ...booleanField, label: "Show Logo" },
    showName: { ...booleanField, label: "Show Name" },
    showSubtitle: { ...booleanField, label: "Show Subtitle" },
    showDescription: { ...booleanField, label: "Show Description" },
    showCTA: { ...booleanField, label: "Show CTA Button" },
    ctaText: { type: "text", label: "CTA Text" },
    ctaLink: { type: "text", label: "CTA Link" },
    backgroundImage: { type: "text", label: "Background Image URL" },
    overlay: { ...booleanField, label: "Show Overlay" },
    overlayOpacity: {
      type: "number",
      label: "Overlay Opacity",
      min: 0,
      max: 1,
      step: 0.1,
    },
    backgroundColor: { type: "text", label: "Background Color" },
    textColor: { type: "text", label: "Text Color" },
    alignX: {
      type: "select",
      label: "Horizontal Align",
      options: [
        { label: "Left", value: "left" },
        { label: "Center", value: "center" },
        { label: "Right", value: "right" },
      ],
    },
    alignY: {
      type: "select",
      label: "Vertical Align",
      options: [
        { label: "Top", value: "top" },
        { label: "Center", value: "center" },
        { label: "Bottom", value: "bottom" },
      ],
    },
    contentWidth: {
      type: "number",
      label: "Content Max Width (px)",
      min: 320,
      max: 1440,
    },
    gap: { type: "number", label: "Stack Gap (px)", min: 0, max: 120 },
    items: {
      type: "array",
      label: "Grid Items",
      arrayFields: {
        icon: { type: "text", label: "Icon / Emoji" },
        title: { type: "text", label: "Title" },
        body: { type: "textarea", label: "Body" },
      },
    } as any,
  },
  defaultProps: {
    showLogo: true,
    showName: true,
    showSubtitle: true,
    showDescription: true,
    showCTA: true,
    ctaText: "View Events",
    ctaLink: "#events",
    overlay: false,
    overlayOpacity: 0.5,
    backgroundColor: "#17265a",
    textColor: "#ffffff",
    alignX: "left",
    alignY: "center",
    contentWidth: 960,
    gap: 16,
    items: [],
  },
  render: (props: any) => {
    const OrganizationHeroComponent: React.FC = () => {
      const {
        showLogo,
        showName,
        showSubtitle,
        showDescription,
        showCTA,
        ctaText,
        ctaLink,
        backgroundImage,
        overlay,
        overlayOpacity,
        backgroundColor,
        textColor,
        alignX,
        alignY,
        contentWidth,
        gap,
        items,
      } = props;
      // Access rootProps via context
      const { organization } = useRootProps();
      const style: React.CSSProperties = {
        backgroundColor,
        color: textColor,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
        padding: "80px 40px",
        display: "flex",
        alignItems:
          alignY === "top"
            ? "flex-start"
            : alignY === "bottom"
            ? "flex-end"
            : "center",
        justifyContent:
          alignX === "left"
            ? "flex-start"
            : alignX === "right"
            ? "flex-end"
            : "center",
      };

      return (
        <div style={style}>
          {backgroundImage && overlay && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})`,
              }}
            />
          )}
          <div
            style={{
              position: "relative",
              zIndex: 10,
              maxWidth: "1280px",
              margin: "0 auto",
            }}
          >
            {showLogo && organization?.logo && (
              <img
                src={organization.logo}
                alt={organization.name}
                style={{ height: "64px", marginBottom: "24px" }}
              />
            )}
            {showName && (
              <h1
                style={{
                  fontSize: "48px",
                  fontWeight: "bold",
                  marginBottom: "16px",
                }}
              >
                {organization?.name || "Organization Name"}
              </h1>
            )}
            {showSubtitle && organization?.subtitle && (
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: 600,
                  marginBottom: "16px",
                  opacity: 0.9,
                }}
              >
                {organization.subtitle}
              </h2>
            )}
            {showDescription && organization?.description && (
              <p
                style={{
                  fontSize: "18px",
                  marginBottom: "24px",
                  opacity: 0.9,
                  maxWidth: "800px",
                }}
              >
                {organization.description}
              </p>
            )}
            {items && items.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 16,
                  marginTop: 12,
                }}
              >
                {items.map((item: any, idx: number) => (
                  <div
                    key={`${item?.title || "item"}-${idx}`}
                    style={{
                      padding: "12px 14px",
                      borderRadius: 12,
                      backgroundColor: "rgba(255,255,255,0.1)",
                      border: "1px solid rgba(255,255,255,0.16)",
                      display: "flex",
                      gap: 12,
                      alignItems: "flex-start",
                    }}
                  >
                    {item?.icon && (
                      <span style={{ fontSize: 20, lineHeight: "24px" }}>
                        {item.icon}
                      </span>
                    )}
                    <div>
                      {item?.title && (
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>
                          {item.title}
                        </div>
                      )}
                      {item?.body && (
                        <div style={{ opacity: 0.9, fontSize: 14 }}>
                          {item.body}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showCTA && (
              <a
                href={ctaLink}
                style={{
                  display: "inline-block",
                  padding: "12px 24px",
                  backgroundColor: "#ffffff",
                  color: "#17265a",
                  borderRadius: "8px",
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                {ctaText}
              </a>
            )}
          </div>
        </div>
      );
    };
    return <OrganizationHeroComponent />;
  },
};

// Organization About Component
export const OrganizationAbout: ComponentConfig<{
  showMission: boolean;
  showVision: boolean;
  showValues: boolean;
  showAbout: boolean;
  layout: "single-column" | "two-column" | "three-column";
  backgroundColor: string;
  textColor: string;
}> = {
  fields: {
    showMission: { ...booleanField, label: "Show Mission" },
    showVision: { ...booleanField, label: "Show Vision" },
    showValues: { ...booleanField, label: "Show Values" },
    showAbout: { ...booleanField, label: "Show About" },
    layout: {
      type: "select",
      label: "Layout",
      options: [
        { label: "Single Column", value: "single-column" },
        { label: "Two Columns", value: "two-column" },
        { label: "Three Columns", value: "three-column" },
      ],
    },
    backgroundColor: { type: "text", label: "Background Color" },
    textColor: { type: "text", label: "Text Color" },
  },
  defaultProps: {
    showMission: true,
    showVision: true,
    showValues: true,
    showAbout: true,
    layout: "two-column",
    backgroundColor: "#ffffff",
    textColor: "#000000",
  },
  render: (props: any) => {
    const OrganizationAboutComponent: React.FC = () => {
      const {
        showMission,
        showVision,
        showValues,
        showAbout,
        layout,
        backgroundColor,
        textColor,
      } = props;
      const { organization } = useRootProps();
      const gridCols =
        layout === "three-column" ? "3" : layout === "two-column" ? "2" : "1";

      return (
        <div
          style={{ backgroundColor, color: textColor, padding: "60px 40px" }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                gap: "32px",
              }}
            >
              {showMission && organization?.about?.mission && (
                <div>
                  <h3
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      marginBottom: "12px",
                    }}
                  >
                    Mission
                  </h3>
                  <p style={{ color: "#666" }}>{organization.about.mission}</p>
                </div>
              )}
              {showVision && organization?.about?.vision && (
                <div>
                  <h3
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      marginBottom: "12px",
                    }}
                  >
                    Vision
                  </h3>
                  <p style={{ color: "#666" }}>{organization.about.vision}</p>
                </div>
              )}
              {showValues && organization?.about?.values && (
                <div>
                  <h3
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      marginBottom: "12px",
                    }}
                  >
                    Values
                  </h3>
                  <p style={{ color: "#666" }}>{organization.about.values}</p>
                </div>
              )}
              {showAbout && organization?.about?.about && (
                <div
                  style={
                    layout === "three-column" ? {} : { gridColumn: "span 2" }
                  }
                >
                  <h3
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      marginBottom: "12px",
                    }}
                  >
                    About Us
                  </h3>
                  <p style={{ color: "#666" }}>{organization.about.about}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };
    return <OrganizationAboutComponent />;
  },
};

// Organization Events List Component
export const OrganizationEventsList: ComponentConfig<{
  layout: "grid" | "list";
  itemsPerRow: number;
  showImage: boolean;
  showDate: boolean;
  showLocation: boolean;
  showCategory: boolean;
  showAttendees: boolean;
  limit: number;
  backgroundColor: string;
}> = {
  fields: {
    layout: {
      type: "select",
      label: "Layout",
      options: [
        { label: "Grid", value: "grid" },
        { label: "List", value: "list" },
      ],
    },
    itemsPerRow: { type: "number", label: "Items Per Row", min: 1, max: 6 },
    showImage: { ...booleanField, label: "Show Image" },
    showDate: { ...booleanField, label: "Show Date" },
    showLocation: { ...booleanField, label: "Show Location" },
    showCategory: { ...booleanField, label: "Show Category" },
    showAttendees: { ...booleanField, label: "Show Attendees" },
    limit: { type: "number", label: "Limit", min: 1, max: 200 },
    backgroundColor: { type: "text", label: "Background Color" },
  },
  defaultProps: {
    layout: "grid",
    itemsPerRow: 3,
    showImage: true,
    showDate: true,
    showLocation: true,
    showCategory: true,
    showAttendees: true,
    limit: 6,
    backgroundColor: "#ffffff",
  },
  render: (props: any) => {
    const OrganizationEventsListComponent: React.FC = () => {
      const {
        layout,
        itemsPerRow,
        showImage,
        showDate,
        showLocation,
        showCategory,
        showAttendees,
        limit,
        backgroundColor,
      } = props;
      const { events = [] } = useRootProps();
      const displayEvents = (events as ISmallEventProps[]).slice(0, limit);

      if (layout === "list") {
        return (
          <div style={{ backgroundColor, padding: "60px 40px" }}>
            <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
              <h2
                style={{
                  fontSize: "32px",
                  fontWeight: "bold",
                  marginBottom: "32px",
                }}
              >
                Upcoming Events
              </h2>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {displayEvents.map((event) => (
                  <div
                    key={event.id}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      padding: "16px",
                      display: "flex",
                      gap: "16px",
                    }}
                  >
                    {showImage && event.cover_image && (
                      <img
                        src={event.cover_image}
                        alt={event.name}
                        style={{
                          width: "128px",
                          height: "128px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <h3
                        style={{
                          fontSize: "20px",
                          fontWeight: 600,
                          marginBottom: "8px",
                        }}
                      >
                        {event.name}
                      </h3>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "16px",
                          fontSize: "14px",
                          color: "#666",
                        }}
                      >
                        {showDate && (
                          <span>
                            {new Date(event.date).toLocaleDateString()}
                          </span>
                        )}
                        {showLocation && (
                          <span>
                            {event.type === "online"
                              ? "Online"
                              : event.type === "hybrid"
                              ? "Hybrid"
                              : "In Person"}
                          </span>
                        )}
                        {showCategory && event.category && (
                          <span>{event.category}</span>
                        )}
                        {showAttendees && (
                          <span>{event.tickets_sold || 0} attendees</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }

      return (
        <div style={{ backgroundColor, padding: "60px 40px" }}>
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <h2
              style={{
                fontSize: "32px",
                fontWeight: "bold",
                marginBottom: "32px",
              }}
            >
              Upcoming Events
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${itemsPerRow}, 1fr)`,
                gap: "24px",
              }}
            >
              {displayEvents.map((event) => (
                <div
                  key={event.id}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  {showImage && event.cover_image && (
                    <img
                      src={event.cover_image}
                      alt={event.name}
                      style={{
                        width: "100%",
                        height: "192px",
                        objectFit: "cover",
                      }}
                    />
                  )}
                  <div style={{ padding: "16px" }}>
                    <h3
                      style={{
                        fontSize: "18px",
                        fontWeight: 600,
                        marginBottom: "8px",
                      }}
                    >
                      {event.name}
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        fontSize: "14px",
                        color: "#666",
                      }}
                    >
                      {showDate && (
                        <div>{new Date(event.date).toLocaleDateString()}</div>
                      )}
                      {showLocation && (
                        <div>
                          {event.type === "online"
                            ? "Online"
                            : event.type === "hybrid"
                            ? "Hybrid"
                            : "In Person"}
                        </div>
                      )}
                      {showCategory && event.category && (
                        <div>{event.category}</div>
                      )}
                      {showAttendees && (
                        <div>{event.tickets_sold || 0} attendees</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };
    return <OrganizationEventsListComponent />;
  },
};

// Organization Stats Component
export const OrganizationStats: ComponentConfig<{
  showLocations: boolean;
  showExperience: boolean;
  backgroundColor: string;
  textColor: string;
}> = {
  fields: {
    showLocations: { ...booleanField, label: "Show Locations" },
    showExperience: { ...booleanField, label: "Show Years of Experience" },
    backgroundColor: { type: "text", label: "Background Color" },
    textColor: { type: "text", label: "Text Color" },
  },
  defaultProps: {
    showLocations: true,
    showExperience: true,
    backgroundColor: "#f3f4f6",
    textColor: "#000000",
  },
  render: (props: any) => {
    const OrganizationStatsComponent: React.FC = () => {
      const { showLocations, showExperience, backgroundColor, textColor } =
        props;
      const { organization } = useRootProps();
      return (
        <div
          style={{ backgroundColor, color: textColor, padding: "60px 40px" }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div
              style={{ display: "flex", gap: "48px", justifyContent: "center" }}
            >
              {showLocations && (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "48px", fontWeight: "bold" }}>
                    {organization?.number_of_locations || 0}
                  </div>
                  <div style={{ fontSize: "16px", color: "#666" }}>
                    Locations
                  </div>
                </div>
              )}
              {showExperience && (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "48px", fontWeight: "bold" }}>
                    {organization?.years_of_experience || 0}
                  </div>
                  <div style={{ fontSize: "16px", color: "#666" }}>
                    Years of Experience
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };
    return <OrganizationStatsComponent />;
  },
};

// Organization Social Links Component
export const OrganizationSocialLinks: ComponentConfig<{
  showFacebook: boolean;
  showInstagram: boolean;
  showLinkedIn: boolean;
  showTwitter: boolean;
  showYouTube: boolean;
  layout: "horizontal" | "vertical";
  backgroundColor: string;
}> = {
  fields: {
    showFacebook: { ...booleanField, label: "Show Facebook" },
    showInstagram: { ...booleanField, label: "Show Instagram" },
    showLinkedIn: { ...booleanField, label: "Show LinkedIn" },
    showTwitter: { ...booleanField, label: "Show Twitter" },
    showYouTube: { ...booleanField, label: "Show YouTube" },
    layout: {
      type: "select",
      label: "Layout",
      options: [
        { label: "Horizontal", value: "horizontal" },
        { label: "Vertical", value: "vertical" },
      ],
    },
    backgroundColor: { type: "text", label: "Background Color" },
  },
  defaultProps: {
    showFacebook: true,
    showInstagram: true,
    showLinkedIn: true,
    showTwitter: true,
    showYouTube: true,
    layout: "horizontal",
    backgroundColor: "#ffffff",
  },
  render: (props: any) => {
    const OrganizationSocialLinksComponent: React.FC = () => {
      const {
        showFacebook,
        showInstagram,
        showLinkedIn,
        showTwitter,
        showYouTube,
        layout,
        backgroundColor,
      } = props;
      const { organization } = useRootProps();
      const links = [];
      if (showFacebook && organization?.facebook_link)
        links.push({ name: "Facebook", url: organization.facebook_link });
      if (showInstagram && organization?.instagram_link)
        links.push({ name: "Instagram", url: organization.instagram_link });
      if (showLinkedIn && organization?.linkedin_link)
        links.push({ name: "LinkedIn", url: organization.linkedin_link });
      if (showTwitter && organization?.x_link)
        links.push({ name: "Twitter", url: organization.x_link });
      if (showYouTube && organization?.youtube_link)
        links.push({ name: "YouTube", url: organization.youtube_link });

      return (
        <div style={{ backgroundColor, padding: "40px" }}>
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div
              style={{
                display: "flex",
                flexDirection: layout === "vertical" ? "column" : "row",
                gap: "16px",
                justifyContent: "center",
              }}
            >
              {links.map((link) => (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: "12px 24px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    textDecoration: "none",
                    color: "#000",
                  }}
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      );
    };
    return <OrganizationSocialLinksComponent />;
  },
};

// Organization Contact Component
export const OrganizationContact: ComponentConfig<{
  showEmail: boolean;
  showPhone: boolean;
  showLocation: boolean;
  showWebsite: boolean;
  backgroundColor: string;
  textColor: string;
}> = {
  fields: {
    showEmail: { ...booleanField, label: "Show Email" },
    showPhone: { ...booleanField, label: "Show Phone" },
    showLocation: { ...booleanField, label: "Show Location" },
    showWebsite: { ...booleanField, label: "Show Website" },
    backgroundColor: { type: "text", label: "Background Color" },
    textColor: { type: "text", label: "Text Color" },
  },
  defaultProps: {
    showEmail: true,
    showPhone: true,
    showLocation: true,
    showWebsite: true,
    backgroundColor: "#ffffff",
    textColor: "#000000",
  },
  render: (props: any) => {
    const OrganizationContactComponent: React.FC = () => {
      const {
        showEmail,
        showPhone,
        showLocation,
        showWebsite,
        backgroundColor,
        textColor,
      } = props;
      const { organization } = useRootProps();
      return (
        <div
          style={{ backgroundColor, color: textColor, padding: "60px 40px" }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <h2
              style={{
                fontSize: "32px",
                fontWeight: "bold",
                marginBottom: "24px",
              }}
            >
              Contact Us
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "24px",
              }}
            >
              {showEmail && organization?.email && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: "8px" }}>
                    Email
                  </div>
                  <a
                    href={`mailto:${organization.email}`}
                    style={{ color: textColor }}
                  >
                    {organization.email}
                  </a>
                </div>
              )}
              {showPhone && organization?.phone_number && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: "8px" }}>
                    Phone
                  </div>
                  <a
                    href={`tel:${organization.phone_number}`}
                    style={{ color: textColor }}
                  >
                    {organization.phone_number}
                  </a>
                </div>
              )}
              {showLocation && organization?.location && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: "8px" }}>
                    Location
                  </div>
                  <div>
                    {organization.location.address && (
                      <div>{organization.location.address}</div>
                    )}
                    <div>
                      {organization.location.city},{" "}
                      {organization.location.state}{" "}
                      {organization.location.postal_code}
                    </div>
                    <div>{organization.location.country}</div>
                  </div>
                </div>
              )}
              {showWebsite && organization?.website && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: "8px" }}>
                    Website
                  </div>
                  <a
                    href={organization.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: textColor }}
                  >
                    {organization.website}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };
    return <OrganizationContactComponent />;
  },
};
